/**
 * Thumbnail gallery under the board preview (snippets/product-preview-gallery.liquid).
 *
 *  <preview-gallery>  a strip (carousel) or grid of thumbnails. Clicking one opens the image
 *                     full size in the element's own <dialog>, with previous/next, arrow keys
 *                     and swipe. The carousel's prev/next buttons only show when the strip
 *                     actually overflows.
 */
(function () {
  if (customElements.get('preview-gallery')) return;

  class PreviewGallery extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;
      this.track = this.querySelector('[data-pg-track]');
      this.thumbs = Array.from(this.querySelectorAll('[data-pg-thumb]'));
      this.modal = this.querySelector('[data-pg-modal]');
      this.index = 0;
      this.thumbs.forEach((thumb) => thumb.addEventListener('click', () => this.open(Number(thumb.dataset.index) || 0)));
      this.initNav();
      this.initModal();
    }

    // ----- carousel prev/next: shown only when the strip overflows
    initNav() {
      this.nav = this.querySelector('[data-pg-nav]');
      if (!this.nav || !this.track) return;
      const prev = this.nav.querySelector('[data-pg-nav-prev]');
      const next = this.nav.querySelector('[data-pg-nav-next]');
      const step = () => Math.max(this.track.clientWidth * 0.8, 120);
      prev.addEventListener('click', () => this.track.scrollBy({ left: -step(), behavior: 'smooth' }));
      next.addEventListener('click', () => this.track.scrollBy({ left: step(), behavior: 'smooth' }));
      const update = () => {
        const overflow = this.track.scrollWidth > this.track.clientWidth + 1;
        this.nav.hidden = !overflow;
        if (!overflow) return;
        // 2px tolerance: the track's own padding means a snapped "start" can sit at scrollLeft 2.
        prev.disabled = this.track.scrollLeft <= 2;
        next.disabled = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 2;
      };
      this.track.addEventListener('scroll', update, { passive: true });
      if ('ResizeObserver' in window) new ResizeObserver(update).observe(this.track);
      else window.addEventListener('resize', update);
      update();
    }

    // ----- dialog
    initModal() {
      if (!this.modal) return;
      this.img = this.modal.querySelector('[data-pg-img]');
      this.modal.querySelector('[data-pg-close]').addEventListener('click', () => this.close());
      this.modal.querySelector('[data-pg-prev]').addEventListener('click', () => this.step(-1));
      this.modal.querySelector('[data-pg-next]').addEventListener('click', () => this.step(1));
      // Clicking the backdrop (the dialog element itself, outside its children) closes it.
      this.modal.addEventListener('click', (e) => { if (e.target === this.modal) this.close(); });
      this.modal.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.step(-1); }
        else if (e.key === 'ArrowRight') { e.preventDefault(); this.step(1); }
      });
      let startX = null;
      this.modal.addEventListener('touchstart', (e) => { startX = e.touches[0].clientX; }, { passive: true });
      this.modal.addEventListener('touchend', (e) => {
        if (startX === null) return;
        const dx = e.changedTouches[0].clientX - startX;
        startX = null;
        if (Math.abs(dx) > 40) this.step(dx < 0 ? 1 : -1);
      });
      this.img.addEventListener('load', () => this.modal.classList.remove('is-loading'));
      this.modal.addEventListener('close', () => {
        const thumb = this.thumbs[this.index];
        if (thumb) thumb.focus({ preventScroll: true });
      });
    }

    open(index) {
      if (!this.modal) return;
      this.show(index);
      if (typeof this.modal.showModal === 'function') {
        if (!this.modal.open) this.modal.showModal();
      } else {
        this.modal.setAttribute('open', '');
      }
    }

    step(direction) {
      this.show(this.index + direction);
    }

    show(index) {
      const n = this.thumbs.length;
      if (!n) return;
      this.index = ((index % n) + n) % n;
      const thumb = this.thumbs[this.index];

      if (this.img.src !== thumb.dataset.full) this.modal.classList.add('is-loading');
      this.img.style.aspectRatio = thumb.dataset.ar || '';
      if (thumb.dataset.fullSrcset) this.img.srcset = thumb.dataset.fullSrcset;
      this.img.src = thumb.dataset.full;
      this.img.alt = thumb.dataset.name || '';

      this.modal.querySelector('[data-pg-title]').textContent = thumb.dataset.name || '';
      const caption = this.modal.querySelector('[data-pg-caption]');
      caption.textContent = thumb.dataset.caption || '';
      caption.hidden = !thumb.dataset.caption;
      this.modal.querySelector('[data-pg-count]').textContent = n > 1 ? `${this.index + 1} / ${n}` : '';
      this.modal.querySelector('[data-pg-prev]').hidden = n < 2;
      this.modal.querySelector('[data-pg-next]').hidden = n < 2;

      this.thumbs.forEach((t, i) => t.setAttribute('aria-current', i === this.index ? 'true' : 'false'));
      this.revealThumb(thumb);

      // Preload the neighbours so stepping feels instant.
      [this.index - 1, this.index + 1].forEach((i) => {
        const t = this.thumbs[((i % n) + n) % n];
        if (t && t !== thumb) { const p = new Image(); p.src = t.dataset.full; }
      });
    }

    // Scroll the strip (only the strip, never the page) so the current thumbnail is visible.
    revealThumb(thumb) {
      if (!this.track) return;
      const item = thumb.closest('.preview-gallery__item') || thumb;
      const left = item.offsetLeft;
      const right = left + item.offsetWidth;
      const viewLeft = this.track.scrollLeft;
      const viewRight = viewLeft + this.track.clientWidth;
      if (left < viewLeft) this.track.scrollTo({ left: left - 8, behavior: 'smooth' });
      else if (right > viewRight) this.track.scrollTo({ left: right - this.track.clientWidth + 8, behavior: 'smooth' });
    }

    close() {
      if (!this.modal) return;
      if (typeof this.modal.close === 'function' && this.modal.open) this.modal.close();
      else this.modal.removeAttribute('open');
    }
  }

  customElements.define('preview-gallery', PreviewGallery);
})();
